import { type ReactElement, useEffect, useRef } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { isEmpty } from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { ActionIcon, TextInput, type TextInputProps } from '@mantine/core'
import { useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../../terros/icon/MaterialSymbolIcon'
import { type SelectedPlace } from './types'

type Props = Omit<TextInputProps, 'rightSection' | 'onChange'> & {
  onAddressChange: (officeAddress: string) => void
  onPlaceSelected: (place: SelectedPlace | undefined) => void
  withClearButton?: boolean
}

export const OfficeAddressInput = ({
  onAddressChange,
  onPlaceSelected,
  withClearButton = true,
  ...inputProps
}: Props): ReactElement => {
  const { t } = useTranslation()
  const places = useMapsLibrary('places')
  const inputRef = useRef<HTMLInputElement>(null)

  const handlePlaceChanged = (place: google.maps.places.PlaceResult): void => {
    if (isEmpty(place.geometry?.location) || isEmpty(place.formatted_address)) {
      showNotification({
        title: t('Invalid Address'),
        message: t("Couldn't get a location from Google"),
        color: 'red',
      })
      return
    }
    onPlaceSelected({
      officeAddress: place.formatted_address,
      url: place.url,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
    })
    onAddressChange(place.formatted_address)
  }

  // Google binds the widget to the input imperatively, so the effect must not depend on the
  // callbacks: none of them are stable, and re-running it builds a second widget on the same input
  const handlePlaceChangedRef = useRef(handlePlaceChanged)
  handlePlaceChangedRef.current = handlePlaceChanged

  useEffect(() => {
    if (!places || !inputRef.current) return

    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'geometry', 'url'],
    })

    const listener = autocomplete.addListener('place_changed', () =>
      handlePlaceChangedRef.current(autocomplete.getPlace())
    )

    return () => {
      listener.remove()
    }
  }, [places])

  const showClearButton = withClearButton && !inputProps.disabled

  const clear = (): void => {
    onAddressChange('')
    onPlaceSelected(undefined)
  }

  return (
    <TextInput
      label={t('Office Address')}
      {...inputProps}
      ref={inputRef}
      onChange={(event) => onAddressChange(event.currentTarget.value)}
      rightSection={
        showClearButton && (
          <ActionIcon aria-label={t('Clear address')} variant='subtle' onClick={clear}>
            <MaterialSymbolIcon icon='close' className='size-[70%]' />
          </ActionIcon>
        )
      }
    />
  )
}
